/**
 * Connection lifecycle API. Added for the FE↔BE integration (Round 2).
 *
 * Wraps the tenant connection endpoints:
 *   POST /connections                       — connect (OAuth / API key / Bearer)
 *   POST /connections/:id/reauthorize       — re-auth an expired/revoked connection
 *   POST /connections/:id/revoke            — revoke a connection
 *
 * The BE stores credentials by reference via its secret store; the request just
 * carries a `credentials` record. In dev (SECRET_STORE_PROVIDER=dev) any record
 * is accepted, so we send a minimal placeholder keyed by auth method — the FE
 * never holds real secrets, matching the canonical "credentials by reference"
 * model. (When real auth lands, the OAuth callback / key input fills this.)
 */
import { apiFetch } from './client';
import type { AuthMethod } from '../domain/types';

interface BeConnectionResponse {
  connection_id: string;
  integration_id: string;
  vendor_version: string;
  auth_method: string;
  status: string;
  connected_at: string;
}

/**
 * Build a placeholder credentials record. WHY a placeholder: the BE requires a
 * `credentials` object, but in local dev the secret store is an in-memory stub
 * that only needs *a* reference. The real secret never transits the FE. For
 * apikey/bearer we echo the value the user pasted; for oauth we send a marker
 * (a real impl would exchange an OAuth code server-side).
 */
function credentialsFor(method: AuthMethod, value?: string): Record<string, unknown> {
  if (method === 'apikey') return { api_key: value || 'dev-api-key' };
  if (method === 'bearer') return { token: value || 'dev-bearer-token' };
  return { oauth: 'dev-oauth-grant' }; // oauth
}

export async function createConnection(
  integrationId: string,
  vendorVersion: string,
  authMethod: AuthMethod,
  value?: string,
): Promise<BeConnectionResponse> {
  return apiFetch<BeConnectionResponse>('/connections', {
    method: 'POST',
    body: {
      integration_id: integrationId,
      vendor_version: vendorVersion,
      auth_method: authMethod,
      credentials: credentialsFor(authMethod, value),
    },
  });
}

export async function reauthorizeConnection(
  connectionId: string,
  method: AuthMethod = 'oauth',
  value?: string,
): Promise<BeConnectionResponse> {
  return apiFetch<BeConnectionResponse>(`/connections/${connectionId}/reauthorize`, {
    method: 'POST',
    body: { credentials: credentialsFor(method, value) },
  });
}

// --- MCP OAuth (Round: MCP-OAuth) ---

/**
 * Begin the MCP OAuth flow for an OAuth-protected connector (Zoho/Stripe/…).
 * Returns the vendor consent URL; the caller opens it (popup) so the user can
 * approve. The BE callback completes the exchange and activates the connection.
 */
export async function startMcpOAuth(integrationId: string): Promise<{ authorizationUrl: string; state: string }> {
  return apiFetch<{ authorizationUrl: string; state: string }>(
    `/connections/${integrationId}/oauth/start`,
    { internal: true },
  );
}

/**
 * Begin REST OAuth (Approach B, SKBRIDGE-ARCH-001 D-1) for a custom/openapi
 * connector's own REST API (e.g. HubSpot's api.hubapi.com), as opposed to
 * startMcpOAuth above (MCP transport). Without this, connecting a REST vendor
 * fell through to the generic createConnection path, which stores a fake
 * `{oauth: 'dev-oauth-grant'}` placeholder instead of a real token — the bug
 * that broke HubSpot's mapping/auto after a reconnect.
 */
export async function startRestOAuth(integrationId: string): Promise<{ authorizationUrl: string; state: string }> {
  return apiFetch<{ authorizationUrl: string; state: string }>(
    `/connections/${integrationId}/rest-oauth/start`,
    { internal: true },
  );
}

export async function revokeConnection(connectionId: string): Promise<BeConnectionResponse> {
  return apiFetch<BeConnectionResponse>(`/connections/${connectionId}/revoke`, {
    method: 'POST',
  });
}

/**
 * List the REAL tools advertised by an OAuth-MCP connection's live server. Runs
 * a headless authenticated MCP session on the backend (listTools) and returns
 * the actual vendor tool names — what the Manage-MCP drawer should show instead
 * of category-based placeholders. Tenant-scoped (the connection belongs to a
 * tenant), so no `internal` flag.
 */
export async function listMcpTools(connectionId: string): Promise<{ tools: string[] }> {
  return apiFetch<{ tools: string[] }>(`/connections/${connectionId}/mcp/tools`, {
    method: 'GET',
  });
}
